import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sharedAPI } from '../../lib/walletApi';
import { Button } from '../../components/ui/button';
import { Loader2, ArrowLeft, Mail, RefreshCw, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'new-password' | 'done'
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [resending, setResending] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sharedAPI.forgotPassword(email);
      setStep('otp');
      setCountdown(60);
      toast.success('Reset code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value[value.length - 1];
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = (e) => {
    e?.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setOtpCode(code);
    setStep('new-password');
  };

  // Auto-advance when all 6 digits entered
  useEffect(() => {
    if (otp.every(d => d !== '') && step === 'otp') {
      const code = otp.join('');
      setOtpCode(code);
      setStep('new-password');
    }
  }, [otp, step]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await sharedAPI.resetPassword(email, otpCode, newPassword);
      setStep('done');
      toast.success('Password reset successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
      if (err.response?.status === 401) {
        setStep('otp');
        setOtp(['', '', '', '', '', '']);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await sharedAPI.forgotPassword(email);
      setCountdown(60);
      toast.success('New code sent');
    } catch { toast.error('Failed to resend'); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden" data-testid="forgot-password-page">
      {/* LEFT PANEL */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800"
          alt="Modern architecture"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#D64D7A]/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <button onClick={() => navigate('/')} className="flex items-center gap-2.5 group">
            <img src="/pebbles-logo.svg" alt="Pebbles" className="h-8 brightness-0 invert group-hover:scale-105 transition-transform" />
          </button>
          <div className="max-w-md">
            <h2 className="text-5xl font-extrabold text-white leading-[1.15] tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Reset your password
            </h2>
            <p className="text-white/70 text-lg mt-5 leading-relaxed">
              We'll send a verification code to your email to help you securely reset your password.
            </p>
          </div>
          <div />
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex-1 flex flex-col bg-white relative z-10">
        <div className="flex items-center justify-between px-8 pt-6">
          <button onClick={() => navigate('/login')} className="flex items-center gap-1.5 text-sm text-[#6b7280] hover:text-[#1a1d1f] transition-colors" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-[420px]">

            {step === 'email' && (
              <>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-xl bg-[#D64D7A]/10 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-7 h-7 text-[#D64D7A]" />
                  </div>
                  <h1 className="text-3xl font-extrabold text-[#1a1d1f] mb-2" style={{ fontFamily: 'Manrope' }} data-testid="forgot-heading">
                    Forgot password?
                  </h1>
                  <p className="text-[#6b7280]">Enter your email and we'll send you a reset code</p>
                </div>
                <form onSubmit={handleSendCode} className="space-y-5">
                  <input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="Email Address" required
                    className="w-full h-[52px] px-4 rounded-lg border border-[#d1d5db] bg-white text-[15px] text-[#1a1d1f] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
                    data-testid="forgot-email-input"
                  />
                  <Button type="submit" disabled={loading} data-testid="send-reset-code-btn"
                    className="w-full h-[52px] rounded-lg bg-[#D64D7A] hover:bg-[#c4406b] text-white text-base font-semibold shadow-[0_4px_16px_rgba(214,77,122,0.3)]">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Code'}
                  </Button>
                </form>
              </>
            )}

            {step === 'otp' && (
              <div className="space-y-6">
                <button onClick={() => { setStep('email'); setOtp(['','','','','','']); }}
                  className="flex items-center gap-1.5 text-sm text-[#6b7280] hover:text-[#1a1d1f] transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <div className="text-center space-y-2">
                  <div className="w-14 h-14 rounded-xl bg-[#D64D7A]/10 flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-7 h-7 text-[#D64D7A]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1a1d1f]" data-testid="otp-heading">Check your email</h2>
                  <p className="text-[#6b7280] text-sm">
                    We sent a 6-digit code to <span className="text-[#1a1d1f] font-medium">{email}</span>
                  </p>
                </div>
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <div className="flex justify-center gap-2.5" onPaste={handleOtpPaste}>
                    {otp.map((digit, i) => (
                      <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric"
                        value={digit} onChange={e => handleOtpChange(i, e.target.value)}
                        onKeyDown={e => handleOtpKeyDown(i, e)} maxLength={1}
                        data-testid={`reset-otp-${i}`}
                        className="w-12 h-14 text-center text-xl font-bold rounded-lg border border-[#d1d5db] bg-white text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
                      />
                    ))}
                  </div>
                  <Button type="submit" disabled={otp.some(d => !d)} data-testid="verify-reset-otp-btn"
                    className="w-full h-[52px] rounded-lg bg-[#D64D7A] hover:bg-[#c4406b] text-white text-base font-semibold">
                    Continue
                  </Button>
                </form>
                <div className="text-center">
                  {countdown > 0 ? (
                    <p className="text-sm text-[#6b7280]">Resend code in <span className="text-[#1a1d1f] font-medium">{countdown}s</span></p>
                  ) : (
                    <button onClick={handleResend} disabled={resending} data-testid="resend-reset-code"
                      className="text-sm text-[#D64D7A] hover:text-[#c4406b] font-medium flex items-center gap-1.5 mx-auto transition-colors">
                      {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Resend code
                    </button>
                  )}
                </div>
              </div>
            )}

            {step === 'new-password' && (
              <>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-xl bg-[#D64D7A]/10 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-7 h-7 text-[#D64D7A]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1a1d1f]" data-testid="new-password-heading">Set new password</h2>
                  <p className="text-[#6b7280] text-sm">Your new password must be at least 8 characters</p>
                </div>
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <input
                    type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="New password" required minLength={8}
                    className="w-full h-[52px] px-4 rounded-lg border border-[#d1d5db] bg-white text-[15px] text-[#1a1d1f] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
                    data-testid="new-password-input"
                  />
                  <input
                    type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password" required minLength={8}
                    className="w-full h-[52px] px-4 rounded-lg border border-[#d1d5db] bg-white text-[15px] text-[#1a1d1f] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
                    data-testid="confirm-password-input"
                  />
                  <Button type="submit" disabled={loading} data-testid="reset-password-btn"
                    className="w-full h-[52px] rounded-lg bg-[#D64D7A] hover:bg-[#c4406b] text-white text-base font-semibold shadow-[0_4px_16px_rgba(214,77,122,0.3)]">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}

            {step === 'done' && (
              <div className="text-center space-y-6" data-testid="reset-success">
                <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-7 h-7 text-emerald-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#1a1d1f] mb-2">Password reset!</h2>
                  <p className="text-[#6b7280] text-sm">Your password has been changed successfully. You can now sign in with your new password.</p>
                </div>
                <Button onClick={() => navigate('/login')} data-testid="go-to-login-btn"
                  className="w-full h-[52px] rounded-lg bg-[#D64D7A] hover:bg-[#c4406b] text-white text-base font-semibold">
                  Sign In
                </Button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
